import type { OnboardingEmailTemplate } from '../OnboardingEmailModal.types';
import { documentItem, optional, questionItem } from './templateItems';

const ENGAGEMENT_DETAILS = 'Engagement Details';
const COMPANY_DOCUMENTS = 'Company Documents/Information';
const VIU = 'Value in Use ("VIU") - Financial Projections';
const FVLCOD = 'Fair Value Less Cost of Disposal ("FVLCOD")';
const DEVELOPED_TECHNOLOGY = 'Developed Technology';
const TRADE_NAME = 'Trade Name / Trademarks';
const CUSTOMER_RELATIONSHIPS = 'Customer Relationships';
const INTERNAL_USE_SOFTWARE = 'Internal Use Software';
const NON_COMPETES = 'Non-Competes';
const LEASES = 'Favorable/Unfavorable Leases';
const ORDER_BACKLOG = 'Order Backlog';
const DEFERRED_REVENUE = 'Deferred Revenue';
const ASSEMBLED_WORKFORCE = 'Assembled Workforce';
const OTHER_INTANGIBLES = 'Other Intangible Assets';

// Based on the "Intangible Impairment Request" tab of the "IAS 36 - Goodwill and Intangible Asset
// Impairment" workbook. Shares the IAS 36 Attio type with the goodwill template, which is
// preselected; once the lead picks this one, it is kept on reopen.
export const IAS_36_INTANGIBLE_IMPAIRMENT_TEMPLATE: OnboardingEmailTemplate = {
  id: 'ias-36-intangible-impairment',
  label: 'Long-Lived Intangible Asset Impairment (IAS 36)',
  emailSubject: 'Information request for your IAS 36 intangible asset impairment analysis',
  valuationTypes: ['Impairment (IAS 36)'],
  requestValuationType: 'ias_36_intangible_impairment',
  introduction: `Hello {{CONTACT_FIRST_NAME}},

Thank you for partnering with us. We are ready to move forward with the IAS 36 long-lived intangible asset impairment testing.

To help us build an accurate model and keep things moving efficiently, please reply to this email with your responses to the questions below and upload the requested documents to the portal. Where available, Excel versions are strongly preferred to ensure precision in our analysis.`,
  documents: [
    questionItem(ENGAGEMENT_DETAILS, 'Company name', 'Company Name'),
    questionItem(
      ENGAGEMENT_DETAILS,
      'CGUs',
      'Number of Cash Generating Units ("CGUs") and name of each CGU to which the intangible asset(s) belong'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'International CGUs',
      'Name(s) of the internationally based CGU(s) where each long-lived intangible asset resides and the country/(ies) where each is based'
    ),
    questionItem(ENGAGEMENT_DETAILS, 'Measurement date', 'Measurement Date'),
    questionItem(ENGAGEMENT_DETAILS, 'Auditor', 'Auditor'),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Intangibles tested & carrying amount',
      'Intangible asset(s) subject to impairment testing (e.g., Trade Name) and carrying amount as of the Measurement Date'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Standard of value (VIU or FVLCOD)',
      'Standard of Value - Should we use value in use ("VIU") or fair value less cost of disposal ("FVLCOD") to calculate the recoverable amount?',
      {
        description:
          'if the selected standard results in an indication below the carrying amount, we will discuss with you before preparing an analysis under the second standard',
      }
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Triggering event',
      'Triggering Event - Please describe the triggering event resulting in impairment testing of the long-lived intangible assets'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Book value as proxy?',
      'Confirm book value is a reasonable proxy for all other balance sheet assets and liabilities of the CGU'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Plans to sell/dispose?',
      'Does Management have any immediate plans to sell or dispose of the intangible asset(s) as of the Measurement Date?'
    ),

    documentItem(
      COMPANY_DOCUMENTS,
      'Business plan / investment presentation',
      'Business plan or investment presentation for each CGU as of the Measurement Date',
      { description: 'if available' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Public comparables',
      'A list of public competitors and public comparable firms for each CGU',
      { description: 'if available' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - LTM',
      'Last twelve months ("LTM") income statement, balance sheet, and cash flow statement for each CGU where the intangible assets reside',
      {
        description:
          'as of the Measurement Date, with all amounts allocated from Corporate (both income statement and balance sheet); preferably in Excel',
      }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - Historical, last 3 years',
      'Historical annual income statements, balance sheets, and cash flow statements for each CGU where each intangible asset resides',
      { description: 'for three previous fiscal years if available; preferably in Excel' }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Balance sheet items not assumed',
      'In a hypothetical sale of the intangible asset, would any balance sheet items not be assumed by a willing buyer?',
      { description: 'such as intercompany accounts or related party loans, as applicable' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Balance sheet with intangibles',
      'Balance sheet as of the Measurement Date for each CGU with the intangible asset(s) detailed by type'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Actuals vs. budget',
      'Comparison of income statement actuals versus budget for the current fiscal year end ("FYE") for each CGU',
      { description: 'if available; please briefly explain any major variances' }
    ),

    documentItem(VIU, 'VIU projections', 'VIU - Controlling projections for the next five years for each CGU', {
      ...optional,
      description:
        'entity-specific basis reflecting the current condition of the asset and excluding uncommitted enhancements; detail COGS, SG&A, R&D, depreciation, amortization, and capital expenditures; if available, base, optimistic, and pessimistic cases with probabilities; preferably in Excel',
    }),
    questionItem(
      VIU,
      'Maintenance capex %',
      'VIU - Percentage of capital expenditures related to maintenance efforts over the projection period',
      optional
    ),
    questionItem(
      VIU,
      'Strategy & assumptions',
      'VIU - Assumptions behind the projections and the market or business strategy for the CGU products/services',
      optional
    ),

    documentItem(
      FVLCOD,
      'FVLCOD projections',
      'FVLCOD - Controlling projections for the next five years for each CGU (market participant basis)',
      {
        ...optional,
        description:
          'detail COGS, SG&A (stock-based compensation may not apply under VIU, check with auditor), R&D, depreciation, amortization, and capital expenditures; if available, base, optimistic, and pessimistic cases with probabilities; preferably in Excel',
      }
    ),
    questionItem(
      FVLCOD,
      'Costs of disposal',
      'FVLCOD - Costs that would be incurred in a hypothetical sale of the intangible asset(s)',
      {
        ...optional,
        description:
          'assuming the sale takes place at the end of the remaining economic useful life; e.g., legal costs, costs of removing the asset, or other direct incremental costs. Please advise the amount and what these costs relate to',
      }
    ),

    questionItem(DEVELOPED_TECHNOLOGY, 'Description', 'Developed Technology - Description of asset', optional),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Multiple technologies?',
      'Are there multiple Developed Technologies with different levels of profitability and/or remaining economic useful lives?',
      { ...optional, description: 'if so, we will have some follow-up questions' }
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Annual obsolescence rate',
      'Developed Technology - Annual Obsolescence Rate',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Estimated remaining useful life',
      'Developed Technology - Estimated Remaining Useful Life',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      '% R&D expansionary',
      '% of R&D Expense related to expansionary purposes (i.e., new products & new features)',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      '% R&D maintenance',
      '% of R&D Expense related to maintaining existing technology',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Technology licensed?',
      'Has the technology been licensed? If so, at what rate?',
      optional
    ),

    questionItem(
      TRADE_NAME,
      'List of trade names',
      'List of Trade Names/Trademarks of the CGU, including Domain Names',
      optional
    ),
    questionItem(
      TRADE_NAME,
      '% revenue from trade name',
      'Confirm 100% of projected revenues for the CGU are attributable to the Trade Name',
      { ...optional, description: 'if not, please provide the appropriate percentage' }
    ),
    questionItem(TRADE_NAME, 'Name recognition', 'Name recognition of the Trade Name as of the Measurement Date', {
      ...optional,
      description: 'has name recognition declined? If so, please discuss the contributing factors',
    }),
    questionItem(TRADE_NAME, 'Profitability change', 'Has the profitability of the Trade Name changed?', optional),
    questionItem(
      TRADE_NAME,
      'Trade name projection risk',
      'Riskiness of Management projections associated with achieving the projections for the Trade Name',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Trade name other risks',
      'Any other risk factors we need to consider related to the Trade Name',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Plan for name',
      'Plan for Name (i.e., sunset over a defined time period or continue to use moving forward)',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Time to transition',
      'Expected time to transition if a transition for the Trade Name is planned',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Trade name licensed?',
      'Has the Trade Name been licensed? If so, at what rate?',
      optional
    ),

    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Relationship type',
      'Are the customer relationships contractual, recurring, transactional, etc.?',
      optional
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Customer profitability/useful lives',
      'Are there customers with different levels of profitability and/or remaining economic useful lives?',
      { ...optional, description: 'if so, we will have some follow-up questions' }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Attrition history & expectations',
      'What has customer attrition been historically, and what are the expectations going forward?',
      optional
    ),
    documentItem(
      CUSTOMER_RELATIONSHIPS,
      'Historical attrition analysis',
      'Customer Relationships - Historical attrition analysis or revenue data by customer type',
      { ...optional, description: '3-5 years, to determine a historical attrition rate' }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      '% revenue from existing customers',
      'What % of total revenue for the first and second fiscal years of the projections is attributable to Customer Relationships?',
      {
        ...optional,
        description:
          'what are the expectations going forward? Please also provide revenue growth attributable to each Customer Relationship over the forecast',
      }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'S&M split',
      'What % of the S&M budget is attributable to maintaining existing Customer Relationships versus acquiring new Customers?',
      optional
    ),

    questionItem(
      INTERNAL_USE_SOFTWARE,
      'Sold to customers?',
      'Is the internal use software sold to customers/clients generating revenue?',
      optional
    ),
    questionItem(
      INTERNAL_USE_SOFTWARE,
      'Sold separately?',
      'Could the internal use software be sold separate to the existing technology offering?',
      optional
    ),
    documentItem(INTERNAL_USE_SOFTWARE, 'Internal Use Software template', 'Completed Internal Use Software template', {
      ...optional,
      description: 'estimated total investment and time period (in years) to recreate the software from scratch',
    }),

    questionItem(
      NON_COMPETES,
      'Covenantors & remaining term',
      'With whom does the Non-Compete apply, and what is the remaining term of the agreement as of the Measurement Date?',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Probability to compete',
      'Probability that the covenantor competes if a non-compete was not in place',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Revenue impact',
      'Impact to revenue if the covenantor competes over the remaining term of the agreement (as a % of revenue)',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Covenantor equity/earnout?',
      'Did any covenantors receive equity as part of the original transaction or are eligible for contingent consideration?',
      optional
    ),

    documentItem(
      LEASES,
      'Off-market lease agreements',
      'Lease agreements not expected to be at market rates (i.e., favorable/unfavorable)',
      optional
    ),
    questionItem(LEASES, 'Contract details', 'Lease contract details', optional),
    questionItem(
      LEASES,
      'CGU economic rate/IBR',
      'Current Economic Rate/Incremental Borrowing Rate (IBR) of the CGU',
      optional
    ),

    questionItem(ORDER_BACKLOG, 'Backlog amount', 'Total amount of Order Backlog as of the Measurement Date', optional),
    questionItem(
      ORDER_BACKLOG,
      'Backlog fulfillment period',
      'Projected annual fulfillment period of backlog (i.e., waterfall)',
      optional
    ),
    questionItem(ORDER_BACKLOG, 'Probability of fulfillment', 'Probability of backlog fulfillment', optional),

    questionItem(
      DEFERRED_REVENUE,
      'Deferred revenue amount',
      'Total deferred revenue assumed as of the Measurement Date',
      optional
    ),
    questionItem(
      DEFERRED_REVENUE,
      'Deferred revenue fulfillment period',
      'Projected annual fulfillment period of deferred revenue (i.e., waterfall)',
      optional
    ),

    documentItem(ASSEMBLED_WORKFORCE, 'Assembled Workforce template', 'Completed Assembled Workforce template', {
      ...optional,
      description:
        'employees in place at the CGU as of the Measurement Date; entity-specific assumptions under VIU, market participant assumptions under FVLCOD',
    }),

    questionItem(
      OTHER_INTANGIBLES,
      'Other intangible assets',
      'Any other Intangible Assets not stated above that may require a separate impairment analysis?',
      { description: 'please include a description' }
    ),
  ],
};
